import type { Todo } from "@listto/powersync";
import { effectiveReminderTime } from "./scheduling";

export interface ScheduledReminder {
  id: string; // The OS notification identifier
  todoId: string;
  /**
   * The `due_date` the OS notification was scheduled for. When provided and it
   * no longer matches the todo's current `due_date`, the reminder is
   * re-scheduled so it fires at the new time. Optional for backends that can't
   * report it (the reminder is then only cancelled when the todo disappears).
   */
  dueDate?: string | null;
}

export interface ReminderDiff {
  toSchedule: Todo[];
  toCancel: string[]; // OS identifiers
}

export const MAX_SCHEDULED_REMINDERS = 60;

const FNV_OFFSET_BASIS = 2_166_136_261;
const FNV_PRIME = 16_777_619;
const INT32_MAX = 2_147_483_647;

/**
 * Maps a todo's string UUID to a deterministic positive 31-bit integer.
 *
 * Some OS notification backends (notably Tauri's `plugin-notification`) key
 * scheduled notifications by `i32`, not by string. Hashing deterministically
 * lets us derive the same numeric id from a todo id on every run, so we never
 * double-schedule the same reminder even if our local registry is lost.
 * Collisions across the ≤60 scheduled reminders are astronomically unlikely.
 */
export function reminderNumericId(todoId: string): number {
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < todoId.length; i += 1) {
    // FNV-1a: XOR in the next code unit, then multiply by the prime. XOR is
    // intrinsic to the hash, so the bitwise rule is disabled for this line only.
    // oxlint-disable-next-line eslint/no-bitwise
    hash ^= todoId.codePointAt(i) ?? 0;
    hash = Math.imul(hash, FNV_PRIME);
  }
  // `Math.imul` leaves a signed 32-bit int — normalise into positive i32 range.
  return ((hash % INT32_MAX) + INT32_MAX) % INT32_MAX;
}

/**
 * True when a scheduled reminder's fire time is known and no longer matches the
 * todo's current `due_date` (i.e. the user moved the reminder to a new time).
 */
function reminderTimeChanged(scheduled: ScheduledReminder, todo: Todo): boolean {
  return scheduled.dueDate !== undefined && scheduled.dueDate !== effectiveReminderTime(todo);
}

/**
 * Calculates the difference between currently scheduled notifications and the desired state.
 *
 * @param upcomingTodos - List of todos that should have a reminder (already filtered/ordered by the query)
 * @param currentlyScheduled - List of reminders currently registered in the OS
 * @param maxNotifications - Maximum number of notifications to keep scheduled
 */
export function calculateReminderDiff(
  upcomingTodos: Todo[],
  currentlyScheduled: ScheduledReminder[],
  maxNotifications = MAX_SCHEDULED_REMINDERS,
): ReminderDiff {
  const scheduledMap = new Map(currentlyScheduled.map((n) => [n.todoId, n]));
  const todosToSchedule = new Set(upcomingTodos.slice(0, maxNotifications).map((t) => t.id));

  const toSchedule: Todo[] = [];
  const toCancel: string[] = [];

  for (const todo of upcomingTodos) {
    const isNeeded = todosToSchedule.has(todo.id);
    const scheduled = scheduledMap.get(todo.id);

    if (isNeeded && !scheduled) {
      toSchedule.push(todo);
    } else if (isNeeded && scheduled && reminderTimeChanged(scheduled, todo)) {
      // due_date moved → cancel the stale OS notification and re-schedule it
      // so it fires at the new time instead of the old one.
      toCancel.push(scheduled.id);
      toSchedule.push(todo);
    } else if (!isNeeded && scheduled) {
      toCancel.push(scheduled.id);
    }

    if (scheduled) {
      scheduledMap.delete(todo.id);
    }
  }

  // Any remaining items in scheduledMap are "orphans" (todos deleted, completed
  // or whose reminder was removed) — cancel them.
  for (const scheduled of scheduledMap.values()) {
    toCancel.push(scheduled.id);
  }

  return { toSchedule, toCancel };
}

/**
 * Agnostic orchestration for syncing reminders between the DB and the platform's notification system.
 */
export async function syncReminders(options: {
  todos: Todo[];
  currentlyScheduled: ScheduledReminder[];
  schedule: (todo: Todo) => Promise<unknown>;
  cancel: (scheduledId: string) => Promise<unknown>;
  maxNotifications?: number;
}) {
  const { todos, currentlyScheduled, schedule, cancel, maxNotifications } = options;
  const { toSchedule, toCancel } = calculateReminderDiff(
    todos,
    currentlyScheduled,
    maxNotifications,
  );

  const promises: Promise<unknown>[] = [];

  for (const scheduledId of toCancel) {
    promises.push(cancel(scheduledId));
  }

  for (const todo of toSchedule) {
    promises.push(schedule(todo));
  }

  if (promises.length > 0) {
    await Promise.all(promises);
  }
}

/**
 * Checks if a specific todo should trigger a reminder notification now.
 */
export function shouldTriggerReminder(
  todo: Todo,
  options: { now: number; notifiedIds: Set<string> },
): boolean {
  const { now, notifiedIds } = options;
  const triggerISO = effectiveReminderTime(todo);
  if (!triggerISO || notifiedIds.has(todo.id)) {
    return false;
  }

  const dueTime = new Date(triggerISO).getTime();

  // If we are within 10 seconds of the deadline, or it just passed within the last minute
  const WINDOW_BEFORE_MS = 10_000;
  const WINDOW_AFTER_MS = 60_000;

  return dueTime <= now + WINDOW_BEFORE_MS && dueTime >= now - WINDOW_AFTER_MS;
}

/**
 * Orchestrates checking a list of todos for immediate notifications (useful for web/running apps).
 */
export function checkUpcomingReminders(options: {
  todos: Todo[];
  notifiedIds: Set<string>;
  onNotify: (todo: Todo) => void;
}) {
  const { todos, notifiedIds, onNotify } = options;
  const now = Date.now();

  for (const todo of todos) {
    if (shouldTriggerReminder(todo, { now, notifiedIds })) {
      onNotify(todo);
      notifiedIds.add(todo.id);
    }
  }
}
